import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type UploadTaskSnapshot,
} from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import imageCompression from 'browser-image-compression';
import { initFirebase } from '../lib/firebase';
import { productDoc } from '../lib/firestore';
import { ensureFreshToken } from '../lib/ensure-fresh-token';

// ===== ERROR TYPES =====

export type ImageUploadErrorType =
  | 'PROCESSING_FAILED'
  | 'UPLOAD_FAILED'
  | 'NETWORK_ERROR'
  | 'INVALID_FORMAT';

export const IMAGE_ERROR_MESSAGES: Record<ImageUploadErrorType, string> = {
  PROCESSING_FAILED: "Impossible de traiter l'image",
  UPLOAD_FAILED: "Echec du telechargement. Reessayer ?",
  NETWORK_ERROR: "Pas de connexion internet",
  INVALID_FORMAT: "Format non supporte (JPEG, PNG, WebP)",
};

// ===== PROCESSING =====

const FULL_IMAGE_MAX_WIDTH = 1200;
const THUMBNAIL_MAX_WIDTH = 300;
const FULL_IMAGE_MAX_SIZE_MB = 0.5; // 500KB
const THUMBNAIL_MAX_SIZE_MB = 0.05; // 50KB

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/**
 * Validate that the file is an accepted image format
 */
export function isValidImageFormat(file: File): boolean {
  // Check MIME type
  if (ACCEPTED_TYPES.includes(file.type)) {
    return true;
  }
  // Fallback: check extension for HEIC (some browsers don't set MIME type)
  const ext = file.name.toLowerCase().split('.').pop();
  return ext === 'heic' || ext === 'heif';
}

/**
 * Compress and resize an image to the specified dimensions
 * Converts to WebP format when supported
 */
export async function compressImage(
  file: File,
  maxWidth: number,
  maxSizeMB: number
): Promise<Blob> {
  const options = {
    maxSizeMB,
    maxWidthOrHeight: maxWidth,
    useWebWorker: true,
    fileType: 'image/webp' as const,
    // browser-image-compression handles HEIC conversion automatically
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch {
    // If WebP conversion fails (old browsers), try JPEG
    try {
      const jpegOptions = { ...options, fileType: 'image/jpeg' as const };
      const compressedFile = await imageCompression(file, jpegOptions);
      return compressedFile;
    } catch {
      throw new Error('PROCESSING_FAILED');
    }
  }
}

export interface ProcessedImages {
  fullBlob: Blob;
  thumbBlob: Blob;
}

/**
 * Process an image file: compress full version and generate thumbnail
 */
export async function processImage(file: File): Promise<ProcessedImages> {
  if (!isValidImageFormat(file)) {
    throw new Error('INVALID_FORMAT');
  }

  try {
    // Process both in parallel for speed
    const [fullBlob, thumbBlob] = await Promise.all([
      compressImage(file, FULL_IMAGE_MAX_WIDTH, FULL_IMAGE_MAX_SIZE_MB),
      compressImage(file, THUMBNAIL_MAX_WIDTH, THUMBNAIL_MAX_SIZE_MB),
    ]);

    return { fullBlob, thumbBlob };
  } catch (error) {
    if (error instanceof Error && error.message === 'INVALID_FORMAT') {
      throw error;
    }
    throw new Error('PROCESSING_FAILED');
  }
}

/**
 * Process a cropped canvas blob (already cropped, just needs compression)
 */
export async function processCroppedImage(blob: Blob): Promise<ProcessedImages> {
  // Convert blob to File for browser-image-compression
  const file = new File([blob], 'cropped.webp', { type: blob.type || 'image/webp' });

  try {
    const [fullBlob, thumbBlob] = await Promise.all([
      compressImage(file, FULL_IMAGE_MAX_WIDTH, FULL_IMAGE_MAX_SIZE_MB),
      compressImage(file, THUMBNAIL_MAX_WIDTH, THUMBNAIL_MAX_SIZE_MB),
    ]);

    return { fullBlob, thumbBlob };
  } catch {
    throw new Error('PROCESSING_FAILED');
  }
}

// ===== STORAGE PATHS =====

/**
 * Get the storage path for a product's full image
 */
export function getProductImagePath(tenantId: string, productId: string): string {
  return `tenants/${tenantId}/products/${productId}.webp`;
}

/**
 * Get the storage path for a product's thumbnail
 */
export function getProductThumbnailPath(tenantId: string, productId: string): string {
  return `tenants/${tenantId}/products/${productId}_thumb.webp`;
}

/**
 * Get the storage path for a merchant's profile photo
 */
export function getMerchantProfilePhotoPath(tenantId: string): string {
  return `tenants/${tenantId}/profile.webp`;
}

// ===== UPLOAD =====

export interface UploadResult {
  imageUrl: string;
  thumbnailUrl: string;
}

/**
 * Upload product images to Firebase Storage with progress tracking
 */
export async function uploadProductImage(
  tenantId: string,
  productId: string,
  processedImages: ProcessedImages,
  onProgress?: (progress: number) => void
): Promise<UploadResult> {
  // Ensure we have a fresh token before uploading
  await ensureFreshToken();

  const { storage } = initFirebase();

  const fullPath = getProductImagePath(tenantId, productId);
  const thumbPath = getProductThumbnailPath(tenantId, productId);

  const fullRef = ref(storage, fullPath);
  const thumbRef = ref(storage, thumbPath);

  // Upload both images
  // Track progress as average of both uploads
  let fullProgress = 0;
  let thumbProgress = 0;

  const updateProgress = () => {
    if (onProgress) {
      onProgress((fullProgress + thumbProgress) / 2);
    }
  };

  try {
    // Upload full image with progress tracking
    const fullUpload = uploadBytesResumable(fullRef, processedImages.fullBlob, {
      contentType: 'image/webp',
    });

    const fullUploadPromise = new Promise<string>((resolve, reject) => {
      fullUpload.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          fullProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          updateProgress();
        },
        (error) => {
          if (error.code === 'storage/retry-limit-exceeded' || error.code === 'storage/unknown') {
            reject(new Error('NETWORK_ERROR'));
          } else {
            reject(new Error('UPLOAD_FAILED'));
          }
        },
        async () => {
          const url = await getDownloadURL(fullUpload.snapshot.ref);
          resolve(url);
        }
      );
    });

    // Upload thumbnail with progress tracking
    const thumbUpload = uploadBytesResumable(thumbRef, processedImages.thumbBlob, {
      contentType: 'image/webp',
    });

    const thumbUploadPromise = new Promise<string>((resolve, reject) => {
      thumbUpload.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          thumbProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          updateProgress();
        },
        (error) => {
          if (error.code === 'storage/retry-limit-exceeded' || error.code === 'storage/unknown') {
            reject(new Error('NETWORK_ERROR'));
          } else {
            reject(new Error('UPLOAD_FAILED'));
          }
        },
        async () => {
          const url = await getDownloadURL(thumbUpload.snapshot.ref);
          resolve(url);
        }
      );
    });

    const [imageUrl, thumbnailUrl] = await Promise.all([fullUploadPromise, thumbUploadPromise]);

    return { imageUrl, thumbnailUrl };
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === 'NETWORK_ERROR' || error.message === 'UPLOAD_FAILED') {
        throw error;
      }
    }
    throw new Error('UPLOAD_FAILED');
  }
}

/**
 * Upload merchant profile photo to Firebase Storage
 * Compressed and stored at tenants/{tenantId}/profile.webp
 */
export async function uploadMerchantProfilePhoto(
  tenantId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  await ensureFreshToken();

  if (!isValidImageFormat(file)) {
    throw new Error('INVALID_FORMAT');
  }

  const { storage, db } = initFirebase();

  const compressedBlob = await compressImage(file, FULL_IMAGE_MAX_WIDTH, FULL_IMAGE_MAX_SIZE_MB);
  const storagePath = getMerchantProfilePhotoPath(tenantId);
  const storageRef = ref(storage, storagePath);

  return new Promise<string>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, compressedBlob, {
      contentType: 'image/webp',
    });

    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        if (onProgress) {
          onProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        }
      },
      (error) => {
        if (error.code === 'storage/retry-limit-exceeded' || error.code === 'storage/unknown') {
          reject(new Error('NETWORK_ERROR'));
        } else {
          reject(new Error('UPLOAD_FAILED'));
        }
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const tenantRef = doc(db, 'tenants', tenantId);
          await updateDoc(tenantRef, { profilePhoto: url });
          resolve(url);
        } catch {
          reject(new Error('UPLOAD_FAILED'));
        }
      }
    );
  });
}

/**
 * Delete merchant profile photo from Firebase Storage and clear the URL
 */
export async function deleteMerchantProfilePhoto(tenantId: string): Promise<void> {
  await ensureFreshToken();

  const { storage, db } = initFirebase();

  const storagePath = getMerchantProfilePhotoPath(tenantId);
  const storageRef = ref(storage, storagePath);

  await deleteObject(storageRef).catch(() => {});

  const tenantRef = doc(db, 'tenants', tenantId);
  await updateDoc(tenantRef, { profilePhoto: null });
}

/**
 * Update product with image URLs after successful upload
 */
export async function updateProductImageUrls(
  productId: string,
  imageUrl: string,
  thumbnailUrl: string
): Promise<void> {
  await ensureFreshToken();

  const { db, currentUserPromise } = initFirebase();
  await currentUserPromise;

  const ref = productDoc(db, productId);
  await updateDoc(ref, {
    imageUrl,
    thumbnailUrl,
    imageUpdatedAt: new Date().toISOString(),
    // Clear legacy picture field when using uploaded image
    picture: null,
  });
}

/**
 * Delete product images from Firebase Storage
 */
export async function deleteProductImage(
  tenantId: string,
  productId: string
): Promise<void> {
  await ensureFreshToken();

  const { storage, db, currentUserPromise } = initFirebase();
  await currentUserPromise;

  const fullPath = getProductImagePath(tenantId, productId);
  const thumbPath = getProductThumbnailPath(tenantId, productId);

  const fullRef = ref(storage, fullPath);
  const thumbRef = ref(storage, thumbPath);

  // Delete both images (ignore errors if they don't exist)
  await Promise.all([
    deleteObject(fullRef).catch(() => {}),
    deleteObject(thumbRef).catch(() => {}),
  ]);

  // Clear image URLs from product
  const productRef = productDoc(db, productId);
  await updateDoc(productRef, {
    imageUrl: null,
    thumbnailUrl: null,
    imageUpdatedAt: null,
  });
}

// ===== HELPERS =====

/**
 * Get the effective image URL for a product
 * Prefers uploaded imageUrl over legacy picture URL
 */
export function getProductImageUrl(product: { imageUrl?: string; picture?: string }): string | undefined {
  return product.imageUrl || product.picture;
}

/**
 * Get the effective thumbnail URL for a product
 * Falls back to full imageUrl or picture if no thumbnail
 */
export function getProductThumbnailUrl(product: { thumbnailUrl?: string; imageUrl?: string; picture?: string }): string | undefined {
  return product.thumbnailUrl || product.imageUrl || product.picture;
}

/**
 * Check if a product has an uploaded image (vs legacy URL)
 */
export function hasUploadedImage(product: { imageUrl?: string }): boolean {
  return Boolean(product.imageUrl);
}
